import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  InlineInputGroup, Select, Option, Textarea, Input
} from 'barnyard/components';
import { LEVELS } from '~/app/common/constants/levels';
import RuleForm from '../inputs/RuleForm';
import InlineItemSelect from '../inputs/InlineItemSelect';
import InlineManufacturerSelect from '../inputs/InlineManufacturerSelect';
import InlineRegionSelect from '../inputs/InlineRegionSelect';
import InlineSupplierSelect from '../inputs/InlineSupplierSelect';
import InlineCategorySelect from '../inputs/InlineCategorySelect';


const ItemMessageGuidance = (props) => {
  const {
    initialValues,
    onSubmit: propSubmit,
    form,
    ...other
  } = props;

  const initialState = {
    item_ids: initialValues.item_ids ? _.join(initialValues.item_ids) : '',
    excluded_item_ids: initialValues.excluded_item_ids ? _.join(initialValues.excluded_item_ids) : '',
    level: initialValues.level || 'info',
    search: initialValues.search || '',
    regions: initialValues.regions && initialValues.regions.length > 0 ? initialValues.regions : [0],
    message: initialValues.message || '',
    manufacturer_ids: initialValues.manufacturer_ids || [],
    excluded_manufacturer_ids: initialValues.excluded_manufacturer_ids || [],
    supplier_ids: initialValues.supplier_ids || [],
    excluded_supplier_ids: initialValues.excluded_supplier_ids || [],
    category_ids: initialValues.category_ids || [],
    excluded_category_ids: initialValues.excluded_category_ids || []
  };

  const [item_ids, setItemIds] = useState(initialState.item_ids);
  const [excludedItemIds, setExcludedItemIds] = useState(initialState.excluded_item_ids);
  const [supplierIds, setSupplierIds] = useState(initialState.supplier_ids);
  const [excludedSupplierIds, setExcludedSupplierIds] = useState(initialState.excluded_supplier_ids);
  const [level, setLevel] = useState(initialState.level);
  const [search, setSearch] = useState(initialState.search);
  const [regions, setRegions] = useState(initialState.regions);
  const [message, setMessage] = useState(initialState.message);
  const [manufacturer_ids, setManufacturerIds] = useState(initialState.manufacturer_ids);
  const [excludedManufacturerIds, setExcludedManufacturerIds] = useState(initialState.excluded_manufacturer_ids);
  const [categoryIds, setCategoryIds] = useState(initialState.category_ids);
  const [excludedCategoryIds, setExcludedCategoryIds] = useState(initialState.excluded_category_ids);
  const areCategoriesAllowed = useMemo(
    () => !_.isEmpty(supplierIds) || !_.isEmpty(manufacturer_ids),
    [supplierIds, manufacturer_ids]
  );

  const onSubmit = useCallback((event) => {
    event.preventDefault();
    propSubmit({
      kind: 'item_message',
      regions: _.filter(regions),
      value: {
        message,
        level,
        search,
        supplier_ids: supplierIds,
        excluded_supplier_ids: excludedSupplierIds,
        item_ids: _.filter(_.map(_.split(item_ids, ','), val => parseInt(val, 10)), val => val),
        excluded_item_ids: _.filter(_.map(excludedItemIds.split(','), item => parseInt(item, 10))),
        manufacturer_ids,
        excluded_manufacturer_ids: excludedManufacturerIds,
        category_ids: areCategoriesAllowed ? categoryIds : [],
        excluded_category_ids: areCategoriesAllowed ? excludedCategoryIds : [],
      }
    });
  }, [
    item_ids,
    level,
    manufacturer_ids,
    message,
    propSubmit,
    regions,
    search,
    excludedItemIds,
    categoryIds,
    excludedCategoryIds,
    areCategoriesAllowed,
    excludedManufacturerIds,
    supplierIds,
    excludedSupplierIds
  ]);

  const onReset = useCallback(() => {
    setItemIds(initialState.item_ids);
    setLevel(initialState.level);
    setSearch(initialState.search);
    setRegions(initialState.regions);
    setMessage(initialState.message);
    setManufacturerIds(initialState.manufacturer_ids);
    setExcludedItemIds(initialState.excluded_item_ids);
    setCategoryIds(initialState.category_ids);
    setExcludedCategoryIds(initialState.excluded_category_ids);
    setSupplierIds(initialState.supplier_ids);
    setExcludedSupplierIds(initialState.excluded_supplier_ids);
  }, [initialState]);

  return (
    <RuleForm
      {...other}
      onSubmit={onSubmit}
      onReset={onReset}
      isSubmitting={form.is_submitting}
    >
      <InlineRegionSelect
        onChange={setRegions}
        value={regions}
      />
      <InlineItemSelect
        value={item_ids}
        onChange={setItemIds}
      />
      <InlineItemSelect
        label='Excluded Vetcove Item IDS'
        value={excludedItemIds}
        onChange={setExcludedItemIds}
      />
      <InlineSupplierSelect
        placeholder='All suppliers by default'
        showSearch={true}
        mode='multiple'
        label='Suppliers'
        description='Suppliers to be selected'
        value={supplierIds}
        onChange={setSupplierIds}
        include_all={false}
        include_any={false}
      />
      <InlineSupplierSelect
        placeholder='No suppliers excluded by default'
        showSearch={true}
        mode='multiple'
        label='Suppliers to be excluded'
        description='Suppliers to be excluded if they are the only supplier available for a given item'
        value={excludedSupplierIds}
        onChange={setExcludedSupplierIds}
        include_all={false}
        include_any={false}
      />
      <InlineManufacturerSelect
        mode='multiple'
        showSearch={true}
        clearable={true}
        value={manufacturer_ids}
        placeholder='Manufacturers'
        onChange={setManufacturerIds}
      />
      <InlineManufacturerSelect
        label='Excluded Manufacturers'
        description='Manufacturers to be excluded'
        value={excludedManufacturerIds}
        placeholder='No manufacturers excluded by default'
        showSearch={true}
        onChange={setExcludedManufacturerIds}
      />
      <InlineCategorySelect
        label='Categories'
        placeholder='All categories by default'
        description={
          'Categories to be included. You may only include these if you specify '
          + 'suppliers and/or manufacturers.'
        }
        value={areCategoriesAllowed ? categoryIds : initialState.category_ids}
        onChange={setCategoryIds}
        disabled={!areCategoriesAllowed}
      />
      <InlineCategorySelect
        label='Excluded Categories'
        placeholder='No categories excluded by default'
        description={
          'Categories to be excluded. You may only include these if you specify '
          + 'suppliers and/or manufacturers.'
        }
        value={areCategoriesAllowed ? excludedCategoryIds : initialState.excluded_category_ids}
        onChange={setExcludedCategoryIds}
        disabled={!areCategoriesAllowed}
      />
      <InlineInputGroup
        label='Alert Level'
        description='Specify the color of the message'
        InputComponent={Select}
        value={level}
        onChange={setLevel}
      >
        <Option value={LEVELS.DANGER}>Alert Message (red)</Option>
        <Option value={LEVELS.WARNING}>Warning Message (orange)</Option>
        <Option value={LEVELS.INFO}>Information Message (blue)</Option>
        <Option value={LEVELS.SUCCESS}>Positive Message (green)</Option>
      </InlineInputGroup>
      <InlineInputGroup
        label='Message'
        description='Content to show on the item'
        InputComponent={Textarea}
        value={message}
        onChange={setMessage}
        placeholder='Enter message here...'
      />
      <InlineInputGroup
        label='Search'
        description='Search to perform when the message is clicked'
        InputComponent={Input}
        value={search}
        onChange={setSearch}
        placeholder='EX: Rimadyl / Zoetis / Onsior'
      />
    </RuleForm>
  );
};


ItemMessageGuidance.defaultProps = {
  type: 'add',
  initialValues: {},
};

ItemMessageGuidance.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  initialValues: PropTypes.object
};

export default ItemMessageGuidance;
